import cn from "clsx";
import { forwardRef } from "react";

import { FlagType } from "@/features/product/components/product-attributes/product-attributes-renderer";

type ChoiceButtonProps = {
  onInputChange: (value: string) => void;
  isChecked: boolean;
  name: string;
  value: string | number;
  type: "radio" | "checkbox";
  flags?: FlagType[];
  disabled?: boolean;
  styles?: string;
  dataTestId?: string;
  onClick?: () => void;
};

function ChoiceButtonInternal({
  name,
  value,
  onInputChange,
  isChecked,
  type,
  flags,
  disabled = false,
  styles,
  dataTestId,
  onClick,
}: ChoiceButtonProps) {
  return (
    <label
      data-testid={dataTestId}
      className={cn(
        styles,
        "relative box-border flex h-10 max-w-[348px] cursor-pointer items-center justify-center rounded-[28px] px-1 py-1.5 text-sm leading-3 outline md:h-12 md:text-base",
        {
          "bg-white border-grey-60 outline-1": !isChecked,
          "bg-berry-10 border-berry outline-2 outline-berry": isChecked,
          "border-grey-60 outline-1 opacity-40": disabled,
          "hover:bg-grey-10": !isChecked && !disabled,
        },
      )}
    >
      <input
        onChange={(e) => onInputChange(e.target.value)}
        onClick={(e) => {
          if ((e.target as HTMLInputElement).checked && onClick) {
            onClick();
          }
        }}
        type={type}
        name={name}
        disabled={disabled}
        value={value}
        checked={isChecked}
        className="hidden"
      />
      <span>{name}</span>
      {!!flags?.length && (
        <div className="absolute bottom-full flex items-center justify-center gap-x-0.5">
          {flags.map((flag) => (
            <div
              key={flag.text}
              className={cn(
                "flex h-[16px] max-w-[120px] translate-y-1/2 items-center justify-center rounded-[28px] p-2 md:h-[20px] md:max-w-[158px]",
                flag.className,
              )}
            >
              <span className="text-xs text-white md:text-sm">{flag.text}</span>
              {flag.icon}
            </div>
          ))}
        </div>
      )}
    </label>
  );
}

export const ChoiceButton = forwardRef(ChoiceButtonInternal);
